<template>
  <section>
    <pdl-loading page :is-loading="isConfirmLoading" />
    <fieldset class="form-fieldset my-2">
      <legend class="form-fieldset__legend text-lg font-bold" qaid="feedback-product-details__type-label">
        {{ $t('text.productType') }}
      </legend>
      <label for="Bikes" class="radio">
        <input
          id="Bikes"
          v-model="selectedProductType"
          type="radio"
          value="Bikes"
          qaid="feedback-product-details__type-bike"
          checked
          @change="productTypeChanged"
        />
        <span class="control-indicator"></span>
        {{ $t('text.bikes') }}
      </label>
      <label for="parts-and-accessories" class="radio">
        <input
          id="parts-and-accessories"
          v-model="selectedProductType"
          type="radio"
          value="PartsAndAccessories"
          qaid="feedback-product-details__type-parts"
          @change="productTypeChanged"
        />
        <span class="control-indicator"></span>
        {{ $t('text.partsAccessories') }}
      </label>
    </fieldset>

    <hr class="rule my-4" />

    <div class="flex flex-wrap px-1">
      <div v-if="isPartsAndAccessories" key="inputName('sku')" class="w-full lg:w-3/5">
        <div class="form-group" :class="{'has-error': !!getErrorMessage(inputName('sku'))}">
          <label for="sku" class="form-label" qaid="feedback-product-details__sku-label">
            {{ $t('product.variants.sku') }}
          </label>
          <div class="form-group--inline is-required" :class="hasWarning">
            <input
              id="sku"
              v-model="claimLine.sku"
              type="text"
              :maxlength="25"
              class="form-control grow"
              qaid="feedback-product-details__sku"
              :name="inputName('sku')"
            />
            <trek-button
              primary
              form-inline
              class="md:ml-1"
              qaid="feedback-product-detail__lookup"
              @click="processSerialOrSku"
            >
              {{ $t('warrantyClaims.B2B.feedback.skuLookup') }}
            </trek-button>
          </div>
          <div
            v-if="emptyInput"
            key="sku_empty_warning"
            class="form-feedback block text-warning"
            :class="isVisible(emptyInput)"
          >
            {{ $t('warrantyClaims.B2B.enterSKU') }}
          </div>
          <div
            v-if="showSkuNotFound"
            key="sku_warning"
            class="form-feedback block text-warning"
            :class="isVisible(showSkuNotFound)"
          >
            {{ $t('warrantyClaims.B2B.feedback.tryAnother') }}
          </div>
        </div>

        <trek-button v-if="!showProductDetails" secondary qaid="unknown-sku-button" @click="processNoInput">
          {{ $t('warrantyClaims.B2B.feedback.skuUnknown') }}
        </trek-button>
      </div>

      <div v-if="isBike" :key="inputName('serialNumber')" class="w-full lg:w-3/5 px-1">
        <div class="form-group" :class="{'has-error': v$.serialNumber.$error}">
          <label for="serial-number" class="form-label" qaid="warranty-claim-line-details-serial-label">
            {{ $t('productRegistration.serialNumber') }}
            <span class="text-red ml-25">*</span>
          </label>

          <div class="md:form-group--inline is-required" :class="hasWarning">
            <input
              id="serial-number"
              :value="serialNumber"
              type="text"
              required
              class="form-control grow"
              qaid="feedback-product-details__serial"
              :name="inputName('serialNumber')"
              :maxlength="30"
              @input="setSerialNumber"
              @blur="updateWarningMessages"
            />

            <trek-button primary form-inline class="mt-1 md:mt-0 md:ml-1" @click="processSerialOrSku">
              {{ $t('warrantyClaims.B2B.feedback.serialLookup') }}
            </trek-button>
          </div>
          <div v-show="v$.serialNumber.$error" class="form-feedback is-invalid">
            {{ getErrorMessage('serialNumber') }}
          </div>
          <div
            v-if="showSerialNotFound"
            key="serialNumber_warning"
            class="form-feedback block text-warning"
            :class="isVisible(showSerialNotFound)"
          >
            {{ $t('warrantyClaims.B2B.feedback.tryAnother') }}
          </div>
        </div>
      </div>

      <div v-if="showProductDetails" class="w-full lg:w-2/5 px-1">
        <pdl-form-group
          v-model="claimLine.modelName"
          type="text"
          qaid="feedback-product-details__model"
          :name="inputName('model')"
          :label="$t('text.model')"
        />
        <div class="grid grid-cols-12 gap-4">
          <pdl-form-group
            v-if="isBike"
            v-model="claimLine.sku"
            class="col-span-12 lg:col-span-5"
            type="text"
            :maxlength="25"
            qaid="feedback-product-details__sku"
            :name="inputName('sku')"
            :label="$t('product.variants.sku')"
          />
          <pdl-form-group
            v-if="isBike"
            v-model="claimLine.modelYear"
            class="col-span-12 lg:col-span-7"
            type="select"
            qaid="feedback-product-details__year"
            :name="inputName('modelYear')"
            :multi-items="modelYearList"
            :label="$t('contactUs.year')"
          />
        </div>
        <div class="grid grid-cols-12 gap-4">
          <pdl-form-group
            v-model="claimLine.color"
            class="col-span-12 lg:col-span-7"
            type="text"
            qaid="feedback-product-details__color"
            :name="inputName('color')"
            :label="$t('product.variants.colour')"
          />

          <pdl-form-group
            v-model="claimLine.size"
            class="col-span-12 lg:col-span-5"
            type="text"
            qaid="feedback-product-details__size"
            :name="inputName('size')"
            :label="$t('product.variants.size')"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {mapMutations, mapState} from 'vuex';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import TrekButton from '@/components/TrekButton';
import {PdlFormGroup} from '@pedal/pdl-form-group';
import {PdlLoading} from '@pedal/pdl-loading';
import {TrekValidationMixin} from '@/utils/validation/trek-validation-mixin';
import {fetchDataBySerial, fetchDataBySku} from '@/api/warranty-claims.js';
import {PdlToastType} from '@/constants/pdl-toast-type.js';

export default {
  components: {TrekButton, PdlFormGroup, PdlLoading},
  mixins: [TrekValidationMixin],
  setup() {
    return {
      ...TrekValidationMixin.setup(),
    };
  },
  data() {
    return {
      isConfirmLoading: false,
      showSerialNotFound: false,
      showSkuNotFound: false,
      showProductDetails: false,
      validator: null,
      serialValidated: false,
      selectedProductType: 'Bikes',
      emptyInput: false,
      serialNumber: '',
    };
  },

  computed: {
    ...mapState('productFeedback', ['validators', 'feedbackData', 'isSerialNumberPristine']),
    modelYearList() {
      const modelYearList = [];
      if (this.claimLine && this.claimLine.modelYearList) {
        modelYearList.push({value: null, displayName: null});
        this.claimLine.modelYearList.map((year) => {
          modelYearList.push({value: year, displayName: year});
        });
      }
      return modelYearList;
    },
    hasWarning() {
      return this.emptyInput || this.showSerialNotFound || this.showSkuNotFound ? 'has-warning' : '';
    },
    isPartsAndAccessories() {
      return this.claimLine && this.claimLine.productType === 'PartsAndAccessories';
    },
    isBike() {
      return this.claimLine && this.claimLine.productType === 'Bikes';
    },
    claimLine() {
      return get(this.feedbackData, 'claimLines[0]', {});
    },
    isEmptySku() {
      return this.isPartsAndAccessories && this.claimLine.sku === '';
    },
    isemptySerial() {
      return this.isBike && this.claimLine.serialNumber === '';
    },
  },

  validations() {
    const validations = {
      serialNumber: {
        requiredIfPristine: this.trekValidators.requiredUnless(
          'productRegistration.serialNumber',
          this.isSerialNumberPristine
        ),
      },
    };
    return validations;
  },

  mounted() {
    if (
      this.claimLine &&
      ((this.isBike && this.claimLine.serialNumber) || (this.isPartsAndAccessories && this.claimLine.sku))
    ) {
      this.serialValidated = true;
    }
  },
  methods: {
    ...mapMutations('productFeedback', ['setFeedbackData', 'updateFeedbackProperty', 'updateFlag']),
    isVisible(flag) {
      return flag ? 'block' : '';
    },
    inputName(propertyName) {
      return `cl_${this.claimLine.productType}_${propertyName}`;
    },
    updateWarningMessages() {
      this.updateFlag({flagName: 'isSerialNumberPristine', value: false});
      this.v$.$validate().then(() => {
        if (this.v$.serialNumber.$error) this.updateFlag({flagName: 'showErrorCallout', value: true});
        this.clearWarningMessages();
      });
    },
    setSerialNumber($event) {
      this.serialNumber = $event.target.value;
      this.updateFeedbackProperty({
        propName: 'serialNumber',
        value: this.serialNumber,
      });
    },
    productTypeChanged() {
      this.updateFlag({flagName: 'isSerialNumberPristine', value: true});
      this.showSerialNotFound = false;
      this.showSkuNotFound = false;
      this.emptyInput = false;
      this.claimLine.sku = '';
      this.updateFlag({flagName: 'showFeedbackDetails', value: false});
      this.updateFeedbackProperty({
        propName: 'productType',
        value: this.selectedProductType,
      });
      this.v$.$reset();
      this.showProductDetails = false;
    },
    clearWarningMessages() {
      this.showSerialNotFound = false;
      this.showSkuNotFound = false;
    },
    clearFormInputs() {
      this.claimLine.modelName = '';
      if (this.isBike) {
        this.claimLine.modelYear = '';
      }
      this.claimLine.size = '';
      this.claimLine.color = '';
      this.serialValidated = false;
      this.showSkuNotFound = false;
      this.$forceUpdate();
    },
    processNoInput() {
      // Clear old fields
      this.clearFormInputs();
      this.showProductDetails = true;
      this.updateFlag({flagName: 'showFeedbackDetails', value: true});
    },
    processSerialOrSku() {
      // need to run validation for serial lookup
      if (this.isBike) {
        this.updateWarningMessages();
      }
      this.v$.$validate().then(() => {
        this.clearFormInputs();
        if (this.isEmptySku) {
          this.emptyInput = true;
          return;
        }
        this.processSerialOrSkuCall();
      });
    },
    async processSerialOrSkuCall() {
      if ((this.isBike && this.claimLine.serialNumber) || (this.isPartsAndAccessories && this.claimLine.sku)) {
        this.isConfirmLoading = true;
        let data;
        try {
          if (this.isBike) {
            data = await fetchDataBySerial(this.claimLine.serialNumber);
            this.claimLine.sku = '';
          } else if (this.isPartsAndAccessories) {
            data = await fetchDataBySku(this.claimLine.sku);
          }
          if (data) {
            this.handleResponse(data);
          }
        } catch (error) {
          this.showProductDetails = true;
          this.updateFlag({flagName: 'showFeedbackDetails', value: true});
          if (this.isBike) {
            this.serialValidated = false;
            this.showSerialNotFound = true;
            this.showSkuNotFound = false;
            this.$notify({
              type: PdlToastType.ERROR,
              message: this.$t('warrantyClaims.B2B.unableLocateSerial'),
              duration: 5000,
            });
          } else if (this.isPartsAndAccessories) {
            this.showSerialNotFound = false;
            this.showSkuNotFound = true;
            this.$notify({
              type: PdlToastType.ERROR,
              message: this.$t('warrantyClaims.B2B.unableLocateSku'),
              duration: 5000,
            });
          }
        } finally {
          this.isConfirmLoading = false;
        }
      }
    },
    handleResponse(response) {
      if (!isEmpty(response)) {
        this.showProductDetails = true;
        this.updateFeedbackProperty({propName: 'modelName', value: response.modelName});
        if (this.isBike) {
          this.updateFeedbackProperty({propName: 'sku', value: response.sku});
          this.updateFeedbackProperty({propName: 'modelYear', value: response.modelYear});
          this.updateFeedbackProperty({propName: 'modelYearList', value: response.modelYearList});
        }
        this.updateFeedbackProperty({propName: 'color', value: response.color});
        this.updateFeedbackProperty({propName: 'size', value: response.size});
        this.updateFlag({flagName: 'showFeedbackDetails', value: true});
        this.serialValidated = true;
        this.emptyInput = false;
        this.showSkuNotFound = false;
        // Force update since we're modifying deeper attributes than what Vue is watching for
        this.$forceUpdate();
      }
    },
  },
};
</script>
